import { useEffect, useState } from "react";
import { Card, Col, Divider, Row, Typography } from "antd";
import { NoShow, StudentUser } from "@bindeleddet/core/types";
import dayjs from "dayjs";

interface Props {
  student: StudentUser;
  newTermViolation: NoShow | undefined;
}

const { Text } = Typography;

const StudentInfo: React.FC<Props> = ({ student, newTermViolation }) => {
  const [isBanned, setIsBanned] = useState<string>();
  const [isBannedUntil, setIsBannedUntil] = useState<string | undefined>();
  const [hasNoShow, setHasNoShow] = useState<string | undefined>();
  const [noShowActiveUntil, setNoShowActiveUntil] = useState<string | undefined>();

  useEffect(() => {
    // isBanned
    let banned: boolean;
    if (newTermViolation) {
      // If there is a new term violation, the student is banned if the violation led to a ban or if the student is already banned
      banned = newTermViolation.got_banned || student.currently_banned;
    } else {
      // If there is no new term violation, we trust the student's currently_banned property
      banned = student.currently_banned;
    }
    setIsBanned(banned ? "Ja" : "Nei");

    // isBannedUntil
    let bannedUntil;
    if (!banned) {
      // If the student is not banned, there is no banned until date
      bannedUntil = undefined;
    } else if (student.number_of_banned_events_left) {
      // If the student is banned, we show the number of banned events left
      bannedUntil =
        student.number_of_banned_events_left && student.number_of_banned_events_left === 1
          ? "1 arrangement til"
          : `${student.number_of_banned_events_left} arrangementer til`;
    } else if (newTermViolation) {
      // If there is a new term violation that led to the ban, the student is banned for 10 events
      bannedUntil = "10 arrangementer til";
    }
    setIsBannedUntil(bannedUntil);

    // hasNoShow
    let noShow: boolean;
    if (newTermViolation) {
      // If there is a new term violation, the student has a no show
      noShow = true;
    } else {
      // If there is no new term violation, we trust the student's no_show_active_until property
      noShow = !!student.no_show_active_until;
    }
    if (banned) {
      // If the student is banned, we dont show the no show status
      setHasNoShow(undefined);
    } else {
      // If the student is not banned, we show the no show status
      setHasNoShow(noShow ? "Ja, én unna utestengelse" : "Nei");
    }

    // noShowActiveUntil
    let noShowUntil;
    if (newTermViolation) {
      // If there is a new term violation, the no show active until date is 180 days from the violation
      noShowUntil = dayjs(newTermViolation.time).add(180, "days").format("DD.MM.YYYY HH:mm");
    } else {
      // If there is no new term violation, we trust the student's no_show_active_until property
      noShowUntil = student.no_show_active_until
        ? dayjs(student.no_show_active_until).format("DD.MM.YYYY HH:mm")
        : undefined;
    }
    if (banned) {
      // If the student is banned, we dont show the no show active until date
      setNoShowActiveUntil(undefined);
    } else {
      // If the student is not banned, we show the no show active until date
      setNoShowActiveUntil(noShowUntil);
    }
  }, [
    newTermViolation,
    student,
    student.currently_banned,
    student.no_show_active_until,
    student.number_of_banned_events_left,
  ]);

  return (
    <Card title="Informasjon">
      <Row style={{ marginBottom: 12 }}>
        <Col span={12}>
          <Text strong>Brukernavn:</Text>
        </Col>
        <Col span={12}>
          <Text>{student.username}</Text>
        </Col>
      </Row>
      <Divider />
      <Row style={{ marginBottom: 12 }}>
        <Col span={12}>
          <Text strong>Allergier:</Text>
        </Col>
        <Col span={12}>
          <Text>{student.allergies ?? "Ingen"}</Text>
        </Col>
      </Row>
      <Divider />
      {/* TODO make this section use api */}
      <Row style={{ marginBottom: 12 }}>
        <Col span={12}>
          <Text strong>Utestengt:</Text>
        </Col>
        <Col span={12}>
          <Text>{isBanned}</Text>
        </Col>
      </Row>
      {isBannedUntil && (
        <Row style={{ marginBottom: 12 }}>
          <Col span={12}>
            <Text strong>Utestengt i:</Text>
          </Col>
          <Col span={12}>
            <Text>{isBannedUntil}</Text>
          </Col>
        </Row>
      )}
      {hasNoShow && (
        <Row style={{ marginBottom: 12 }}>
          <Col span={12}>
            <Text strong>Prikk:</Text>
          </Col>
          <Col span={12}>
            <Text>{hasNoShow}</Text>
          </Col>
        </Row>
      )}
      {noShowActiveUntil && (
        <Row style={{ marginBottom: 12 }}>
          <Col span={12}>
            <Text strong>Prikk aktiv til:</Text>
          </Col>
          <Col span={12}>
            <Text>{noShowActiveUntil}</Text>
          </Col>
        </Row>
      )}
      <Divider />
      <Row style={{ marginBottom: 12 }}>
        <Col span={12}>
          <Text strong>Aktiv:</Text>
        </Col>
        <Col span={12}>
          <Text>{student.deactivated ? "Nei" : "Ja"}</Text>
        </Col>
      </Row>
      <Divider />
      <Row style={{ marginBottom: 12 }}>
        <Col span={12}>
          <Text strong>Uteksaminert:</Text>
        </Col>
        <Col span={12}>
          <Text>{student.has_graduated ? "Ja" : "Nei"}</Text>
        </Col>
      </Row>
    </Card>
  );
};
export default StudentInfo;
